import type { User } from "firebase/auth";

import { API_BASE_URL } from "./config.ts";

type Referral = { click_id: string } | null;
type ReferralBear = {
  ready: Promise<unknown>;
  getReferral: () => Referral;
  signup: (identifier: string, email?: string) => Promise<{ tracked: boolean }>;
};

declare global {
  interface Window {
    referralBear?: ReferralBear;
    referralBearScriptReady?: Promise<ReferralBear>;
  }
}

export const affiliateTrackingMode = process.env.REACT_APP_AFFILIATE_TRACKING_MODE;
export const usingReferralBear = affiliateTrackingMode === "sandbox" || affiliateTrackingMode === "referralbear";

const referralProgram = affiliateTrackingMode === "sandbox"
  ? "ea177e51-1f08-467e-b260-eb34c992b30f"
  : "48b7c5b7-09ce-4f0b-a65d-9f937720b091";

const newSignupKey = "referral_bear_new_signup_uid";
const unresolvedSignupKey = "referral_bear_unresolved_signup_uid";
let newSignupUid: string | null = null;
let unresolvedSignupUid: string | null = null;

function markNewSignup(uid: string) {
  newSignupUid = uid;
  try { sessionStorage.setItem(newSignupKey, uid); } catch { /* Memory fallback for restricted storage. */ }
  if (pendingReferralEvidence()) {
    unresolvedSignupUid = uid;
    try { sessionStorage.setItem(unresolvedSignupKey, uid); } catch { /* Memory fallback. */ }
  }
}

function clearNewSignup(uid: string) {
  if (newSignupUid === uid) newSignupUid = null;
  if (unresolvedSignupUid === uid) unresolvedSignupUid = null;
  try { if (sessionStorage.getItem(newSignupKey) === uid) sessionStorage.removeItem(newSignupKey); }
  catch { /* Memory fallback for restricted storage. */ }
  try { if (sessionStorage.getItem(unresolvedSignupKey) === uid) sessionStorage.removeItem(unresolvedSignupKey); }
  catch { /* Memory fallback for restricted storage. */ }
}

function unresolvedSignup(uid: string) {
  try { return unresolvedSignupUid === uid || sessionStorage.getItem(unresolvedSignupKey) === uid; }
  catch { return unresolvedSignupUid === uid; }
}

function canRetrySignup(user: User) {
  const createdAt = Date.parse(user.metadata.creationTime ?? "");
  const age = Date.now() - createdAt;
  if (!Number.isFinite(age) || age < 0 || age > 24 * 60 * 60 * 1000) {
    clearNewSignup(user.uid);
    return false;
  }
  try { return newSignupUid === user.uid || sessionStorage.getItem(newSignupKey) === user.uid; }
  catch { return newSignupUid === user.uid; }
}

function validStoredReferral(value: any) {
  return value?.program_id === referralProgram && !value.blocked &&
    typeof value.click_id === "string" && value.expires_at > Date.now();
}

function pendingReferralEvidence() {
  if (new URLSearchParams(window.location.search).has("ref")) return true;
  try {
    const stored = JSON.parse(localStorage.getItem(`otter_referral_${referralProgram}`) ?? "null");
    if (validStoredReferral(stored)) return true;
  } catch { /* The SDK may still have the referral in its shared cookie. */ }
  try {
    const name = `otter_referral_${referralProgram}=`;
    const cookie = document.cookie.split("; ").find((value) => value.startsWith(name));
    return validStoredReferral(JSON.parse(decodeURIComponent(cookie?.slice(name.length) ?? "null")));
  } catch { return false; }
}

async function referralBear(): Promise<ReferralBear> {
  const client = await window.referralBearScriptReady;
  if (!client) throw new Error("Affiliate tracking is unavailable");
  await client.ready;
  return client;
}

export async function prepareAffiliateSignup() {
  if (!usingReferralBear) return;
  try {
    const client = await referralBear();
    if (pendingReferralEvidence() && !client.getReferral())
      throw new Error("Your partner link could not be confirmed. Please try again.");
  }
  catch (error) { if (pendingReferralEvidence()) throw error; }
}

async function merchantAttribution(user: User, path: string, clickId?: string,
  decision?: "direct" | "pending") {
  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE_URL}/api/v2/affiliate-attribution${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(decision ? { decision, ...(clickId ? { click_id: clickId } : {}) } :
      clickId ? { click_id: clickId } : {}),
  });
  if (!response.ok) {
    const result = await response.json().catch(() => ({}));
    const error = new Error(typeof result.error === "string" ? result.error : "Could not confirm your referral");
    Object.assign(error, { status: response.status });
    throw error;
  }
  return response.json();
}

export async function recordNewAffiliateSignup(user: User, isNewUser: boolean) {
  if (!usingReferralBear || !isNewUser) return;
  markNewSignup(user.uid);
  let client: ReferralBear;
  try { client = await referralBear(); }
  catch (error) {
    const pending = pendingReferralEvidence();
    await merchantAttribution(user, "/decision", undefined, pending ? "pending" : "direct");
    if (pending) throw error;
    clearNewSignup(user.uid);
    return;
  }
  const referral = client.getReferral();
  const pending = !!referral || pendingReferralEvidence();
  await merchantAttribution(user, "/decision", referral?.click_id, pending ? "pending" : "direct");
  if (!referral) {
    if (pending) throw new Error("Your partner link could not be confirmed. Please try again.");
    clearNewSignup(user.uid);
    return;
  }
  await client.signup(user.uid, user.email ?? undefined);
  await merchantAttribution(user, "", referral.click_id);
  clearNewSignup(user.uid);
}

export async function confirmStripeAttribution(user: User) {
  if (!usingReferralBear) return null;
  const retrySignup = canRetrySignup(user);
  let referral: Referral = null;
  if (retrySignup) {
    try {
      referral = (await referralBear()).getReferral();
    } catch (error) {
      if (pendingReferralEvidence() || unresolvedSignup(user.uid)) throw error;
      // An already-confirmed referral is still resolved by the merchant server below.
    }
  }
  if (retrySignup && referral) {
    await merchantAttribution(user, "/decision", referral.click_id, "pending");
    await merchantAttribution(user, "", referral.click_id);
  }
  for (let attempt = 0; attempt < 6; attempt++) {
    try {
      const result = await merchantAttribution(user, "/stripe");
      return typeof result.click_id === "string" ? result.click_id : null;
    } catch (error) {
      if ((error as { status?: number }).status === 409 &&
          (error as Error).message === "Signup referral must be confirmed first") {
        if (retrySignup && (pendingReferralEvidence() || unresolvedSignup(user.uid)))
          throw new Error("Your referral could not be confirmed. Please try checkout again.");
        if (retrySignup) clearNewSignup(user.uid);
        return null;
      }
      if ((error as Error).message !== "Stripe customer is not ready yet" || attempt === 5) throw error;
      await new Promise((resolve) => setTimeout(resolve, 1000 + attempt * 500));
    }
  }
  return null;
}
